/* eslint-disable jira/integration/test-id-by-folder-structure */

/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useCallback, useMemo } from 'react';

import { useAiRovoCurrentRouteConfiguration } from '@jira/ai-rovo_actions-store/src/useAiRovoCurrentRouteConfiguration.tsx';
import { Box } from '@atlaskit/primitives/compiled/box';
import { cssMap, jsx } from '@atlaskit/css';
import ConversationAssistant from '@atlassian/conversation-assistant/AsyncConversationAssistant';
import {
	type ConversationAssistantConfig,
	ConversationAssistantUIModes,
} from '@atlassian/conversation-assistant/types';
import { fg } from '@atlassian/jira-feature-gating';
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import { useOrgId } from '@jira/router-resources__navigation-organization-id/src/useOrgId.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api/rovo-experience-api-store';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useRouterActions } from '@atlassian/react-resource-router';
import { getRouterState } from '@atlassian/react-resource-router/src/controllers/router-store/index.tsx';
import { useActiveCompatibleEditor } from './useActiveCompatibleEditor.tsx';
import { useConversationAssistantSidebarApi } from './useConversationAssistantSidebarApi.tsx';

const getProductFromPath = (pathname: string, search: string): 'jsm' | 'jpd' | 'jira' => {
	if (/\/servicedesk\//.test(pathname) || /[?&]types=service_desk/.test(search)) {
		return 'jsm';
	}
	if (pathname.includes('/jira/polaris')) {
		return 'jpd';
	}
	return 'jira';
};

type JiraConversationAssistantProps = {
	onClosePanel: () => void;
	storeInitializerOnly?: boolean;
	children?: React.ReactNode;
	/**
	 * When true, skips the Relay environment provider wrapper in ConversationAssistant.
	 * Use when rendered inside a tree that already has its own Relay environment
	 * (e.g. issue view), to avoid conflicts with Relay fragment reads.
	 */
	skipRelayProvider?: boolean;
};

const SYNTHETIC_HEADER = 'x-issynthetic-traffic';

const ASAN_CLOUD_ID = '7a4256fd-889b-4c18-8d43-536cdd3c89ca';
const ASAN_SUPPORT_COPILOT_AGENT_ID = '770177bc-1c7b-4b90-beb7-8e9dc2f890d7';
const ASAN_OVERRIDE_CONFIG = {
	agentsConfig: {
		filter: [ASAN_SUPPORT_COPILOT_AGENT_ID],
		defaultAgentId: ASAN_SUPPORT_COPILOT_AGENT_ID,
	},
};

export const JiraConversationAssistant = ({
	onClosePanel,
	storeInitializerOnly = false,
	children,
	skipRelayProvider = false,
}: JiraConversationAssistantProps): JSX.Element => {
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const {
		toggleConversationAssistantUIMode,
		isMiniModalAvailable,
		isSidebarAvailable,
		isFullScreenModalAvailable,
	} = useRovoExperienceAPIStore();
	const sidebarApi = useConversationAssistantSidebarApi({});
	const { data: orgId } = useOrgId();
	const { push } = useRouterActions();

	const configuration = useAiRovoCurrentRouteConfiguration();

	const isSyntheticTenant = getMeta('ajs-is-synthetic') === 'true' || fg('is_synthetic_tenant');

	const handleDeepLinkClick = useCallback(
		(url: string) => {
			const urlObj = new URL(url);
			push(urlObj.pathname + urlObj.search + urlObj.hash);
		},
		[push],
	);
	const activeCompatibleEditor = useActiveCompatibleEditor();
	const { pathname, search } = getRouterState().location;
	const product = fg('jsm_jpd_product_attribution') ? getProductFromPath(pathname, search) : 'jira';

	const config: ConversationAssistantConfig = useMemo(
		() => ({
			displaySubmitFlag: true,
			siteId: cloudId,
			activationId,
			product,
			orgId,
			experienceId: 'ai-mate',
			assistanceServiceConfig: {
				// TODO: type `AssistanceServiceConfig` should be decoupled from headers to prevent the need for other required keys which shouldn't need to be defined here (`product` and `experienceId`)
				headers: isSyntheticTenant
					? { [SYNTHETIC_HEADER]: String(isSyntheticTenant) }
					: // oxlint-disable-next-line typescript/consistent-type-assertions
						({} as Record<string, string>),
				// Rename to `urlPrefix` in the package; baseUrl is a deprecated concept
				baseUrl: '/gateway/api/assist',
			},
			...(activeCompatibleEditor?.editorId &&
			activeCompatibleEditor?.containerAri &&
			expVal('jira_maui_remix', 'isEnabled', false)
				? {
						smartCreationContext: {
							editor: {
								containerAri: activeCompatibleEditor.containerAri,
								editorId: activeCompatibleEditor.editorId,
							},
						},
					}
				: {}),
			...(expVal('prevent_abort_jira_rovo_chat', 'isEnabled', false)
				? {
						preventAbortRequestsOnStandaloneChatClose: true,
						preventAbortRequestsOnStagingModalClose: true,
					}
				: {}),
		}),
		[cloudId, activationId, orgId, isSyntheticTenant, activeCompatibleEditor, product],
	);

	const modeConfig = useMemo(
		() => ({
			mode: ConversationAssistantUIModes.SIDEBAR,
			isMiniModalAvailable,
			isSidebarAvailable,
			isFullScreenModalAvailable:
				// asx_full_screen_modal_mode is a temporary kill switch for the full screen modal mode
				// eslint-disable-next-line jira/ff/no-preconditioning
				isFullScreenModalAvailable &&
				fg('asx_full_screen_modal_mode') &&
				expVal('ai_agent_sessions_experience', 'isEnabled', false),
			switchToMode: (mode: ConversationAssistantUIModes | undefined) => {
				toggleConversationAssistantUIMode({ targetMode: mode, sidebarApi });
			},
		}),
		[
			isMiniModalAvailable,
			isSidebarAvailable,
			isFullScreenModalAvailable,
			toggleConversationAssistantUIMode,
			sidebarApi,
		],
	);

	const useStoreInitializerWrapper =
		expVal('jira_free_gen_editor', 'isEnabled', false) && storeInitializerOnly;

	const handleCloseChat = useCallback(() => {
		onClosePanel();
		toggleConversationAssistantUIMode({
			targetMode: undefined,
			sidebarApi,
		});
	}, [onClosePanel, toggleConversationAssistantUIMode, sidebarApi]);

	// ROVOFE-76 - No resize wrapper for nav4 as it uses PanelSplitter now
	return (
		<Box
			xcss={
				useStoreInitializerWrapper
					? styles.storeInitializerWrapper
					: styles.conversationAssistantWrapperNav4
			}
		>
			<ConversationAssistant
				testId="conversation-assistant.ui.conversation-assistant"
				onCloseChat={handleCloseChat}
				uiConfig={{
					customHandlers: {
						handleDeepLinkClick,
					},
					isSplitLayout: expVal('ai_agent_sessions_experience', 'isEnabled', false)
						? true
						: undefined,
				}}
				config={config}
				isNav4Enabled
				modeConfig={modeConfig}
				{...configuration}
				overridingChildren={children ?? null}
				{...(fg('ai_agent_sessions_warming_cache') ? { skipRelayProvider } : {})}
				{...(cloudId === ASAN_CLOUD_ID && fg('asan-rovo-restrict-agents')
					? ASAN_OVERRIDE_CONFIG
					: {})}
			/>
		</Box>
	);
};

const styles = cssMap({
	conversationAssistantWrapperNav4: {
		width: '100%',
		height: '100%',
	},
	storeInitializerWrapper: {
		width: '0px',
		height: '0px',
	},
});
